import React, { PropsWithChildren } from 'react';

import { spacing } from '@rbilabs/universal-components/themes';
import { useRouter } from 'expo-router';
import { ScrollView, ScrollViewProps, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import Animated, { SlideInDown } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ToastProvider } from 'react-native-toast-notifications';
import Toast from 'react-native-toast-notifications';

import { useModalAnalyticsAppearanceEvent } from 'hooks/use-modal-analytics-appearance-event';
import { GlobalAppModals } from 'navigation/global-app-modals';
import { theme } from 'styles/configure-theme';
import { primitive } from 'styles/constants/primitives';
import { WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { isAndroid, isIos, isWeb } from 'utils/environment';

import { CardModalHeader, ModalHeaderProps, STANDARD_BACKGROUND } from './card-modal-header';
import { modalToaster } from './toaster/refs';

interface PartialCardModalProps {
  testID?: string;
  children: React.ReactNode;
  /** automatic analytics event name for modal opening */
  analyticsEvent?: string | null;
  /** add standard padding to toe modal */
  standardPadding?: boolean;
  /** floating footer - usually buttons go here */
  footer?: React.ReactNode;
  footerBackgroundColor?: string;
  disableFooterBorder?: boolean;
  /** make it a scroll view */
  scrollView?: boolean;
  scrollViewRef?: React.RefObject<CustomScrollViewProps | undefined>;
  /** if true always show the header even with no headerText */
  alwaysShowHeader?: boolean;
  /** if true always hide the header even though it has header text */
  alwaysHideHeader?: boolean;
  /** animate the modal entrance on ios/android. set to false on the final state if the modal is used as separate mounts (instances) for loading and final states */
  webAnimate?: boolean;
  /** you should probably use headerText not this unless you really need it */
  _headerOverride?: React.ReactNode;

  /** when needed for nuanced situations... */
  wrappedChildrenStyle?: StyleProp<ViewStyle>;
  /** when needed for nuanced situations... */
  modalContainerStyle?: StyleProp<ViewStyle>;
  /** pages not using CardModal fully (like PDP) might need this flag */
  noDesktopWebMaxWidth?: boolean;

  /** needed for <CardModal> wrapped loading states on deeply nested components to avoid issues  */
  disableGlobalAppModals?: boolean;

  /** when needed for nuanced situations... */
  scrollContainerStyle?: StyleProp<ViewStyle>;
  customHeaderBackgroundColor?: string;

  // REMEMBER many props are on ModalHeaderProps too
}

export type CardModalProps = React.FC<PropsWithChildren<ModalHeaderProps & PartialCardModalProps>>;

export interface CustomScrollViewProps extends ScrollViewProps, ScrollView {}

/**
 * Use with `presentation: 'modal'` in expo-router
 * replaces our old Modal component src/components/modal/index.tsx
 * */
export const CardModal: CardModalProps = ({
  children,
  testID,
  onDismiss,
  headerText,
  analyticsEvent,
  standardPadding = false,
  footer,
  footerBackgroundColor,
  disableFooterBorder = false,
  scrollView = false,
  invertHeaderColor,
  alwaysHideHeader,
  dismissType,
  helmetTitle,
  scrollViewRef,
  alwaysShowHeader,
  customHeaderLeft,
  customHeaderRight,
  headerFlexStartStyleOverride,
  headerFlexEndStyleOverride,
  isLoading,
  _headerOverride,
  wrappedChildrenStyle,
  modalContainerStyle,
  webAnimate = true,
  noDesktopWebMaxWidth,
  isFullScreen = false,
  disableGlobalAppModals = false,
  scrollContainerStyle,
  customHeaderBackgroundColor,
}) => {
  const router = useRouter();
  const { top, bottom } = useSafeAreaInsets();

  const resolvedWebMaxWidthStyle = noDesktopWebMaxWidth ? undefined : WEB_MAX_WIDTH_STYLE;

  useModalAnalyticsAppearanceEvent(analyticsEvent);

  const header = alwaysHideHeader
    ? null
    : _headerOverride
    ? _headerOverride
    : (headerText || alwaysShowHeader) && (
        <CardModalHeader
          {...{
            headerText,
            onDismiss,
            dismissType,
            invertHeaderColor,
            helmetTitle,
            customHeaderLeft,
            customHeaderRight,
            isLoading,
            isFullScreen,
            backgroundColorOverride: customHeaderBackgroundColor,
            headerFlexStartStyleOverride,
            headerFlexEndStyleOverride,
          }}
        />
      );

  const finalFooterBgColor = footerBackgroundColor
    ? { backgroundColor: footerBackgroundColor }
    : null;

  const safePaddingBottom = { paddingBottom: bottom || spacing.$4 };

  const finalFooter = footer && (
    <View
      style={[
        modalStyles.footerWrap,
        safePaddingBottom,
        finalFooterBgColor,
        disableFooterBorder ? { borderTopWidth: 0 } : {},
      ]}
    >
      <View style={[modalStyles.footerInner, resolvedWebMaxWidthStyle]}>{footer}</View>
    </View>
  );

  const finalStandardPadding = standardPadding ? { padding: spacing.$4, paddingTop: 0 } : undefined;

  const wrappedChildren = (
    <View
      style={[
        modalStyles.wrappedChildren,
        isWeb && !footer ? { height: '100%' } : null, // required to make footer work on web only when not using scrollView
        finalStandardPadding,
        wrappedChildrenStyle,
        resolvedWebMaxWidthStyle,
      ]}
    >
      {children}
      {/*
        GlobalAppModals also lives at the root but must live here too on iOS only
        because otherwise they will be hidden behind the "ios floaty modal" and create issues
      */}
      {isIos && !isFullScreen && !disableGlobalAppModals && <GlobalAppModals isModal />}
    </View>
  );

  const finalChildren = scrollView ? (
    <>
      <KeyboardAwareScrollView
        ref={scrollViewRef as any}
        contentContainerStyle={[modalStyles.scrollContainer, scrollContainerStyle]}
        style={modalStyles.scrollStyle}
        keyboardShouldPersistTaps="always"
      >
        {wrappedChildren}
      </KeyboardAwareScrollView>
      {finalFooter}
    </>
  ) : (
    <>
      {wrappedChildren}
      {finalFooter}
    </>
  );

  // iOS is "card style" with expo-router "presentation: 'modal'"" already, no need for much else!
  if (isIos && !isFullScreen) {
    return (
      <ToastProvider>
        <View
          style={[
            modalStyles.iOSWrap,
            finalFooterBgColor,
            // handle a potential situation for deeplinks to a closed app (that seems to have been solved but could come back) where the ios card style isn't applied. this gets it out of the safe area header at least
            !router.canGoBack() ? { marginTop: top } : {},
          ]}
          testID={testID}
        >
          {header}
          {finalChildren}
          <Toast
            ref={x => {
              modalToaster.current = x;
            }}
          />
        </View>
      </ToastProvider>
    );
  }

  // prevents animations on Android
  const ShadowContainerView = (isWeb ? Animated.View : View) as Animated.View;

  // web and android make the modal "card style" similar to iOS
  return (
    <View
      testID="card-modal"
      style={[
        modalStyles.backdrop,
        isFullScreen
          ? invertHeaderColor
            ? { backgroundColor: primitive.bk.$bbqBrown }
            : { backgroundColor: 'transparent' }
          : isWeb
          ? { backgroundColor: primitive.$blackOpacity30 }
          : null,
        // android needs a safe area if no there is footer because we have edge-to-edge mode enabled
        !footer && isAndroid ? safePaddingBottom : null,
      ]}
    >
      <ShadowContainerView
        entering={webAnimate ? SlideInDown : undefined}
        style={[
          modalStyles.shadowContainer,
          { marginTop: isWeb && !isFullScreen ? spacing.$2 : top },
          isFullScreen ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 } : null,
        ]}
      >
        <ToastProvider>
          {header}
          <View
            style={[
              modalStyles.modalContainer,
              modalContainerStyle,
              isFullScreen ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 } : null,
            ]}
          >
            {finalChildren}
          </View>
        </ToastProvider>
        <Toast
          ref={x => {
            modalToaster.current = x;
          }}
        />
      </ShadowContainerView>
    </View>
  );
};

const FOOTER_HIGHLIGHT = 'white';
export const BACKDROP_COLOR = '#9D9890'; // effectively the combination of mayo white with a 'rgba(0, 0, 0, 0.3)' on top

// this only comes into play on desktop web, don't mess with native as it could cause issues
export const WEB_MAX_WIDTH_STYLE = (isWeb
  ? { maxWidth: WEB_SMALL_MAX_WIDTH, width: '100%' }
  : null) as ViewStyle;

export const modalStyles = StyleSheet.create({
  iOSWrap: {
    flex: 1,
    backgroundColor: FOOTER_HIGHLIGHT,
  },
  backdrop: {
    flex: 1,
    backgroundColor: BACKDROP_COLOR,
  },
  shadowContainer: {
    flex: 1,
    backgroundColor: STANDARD_BACKGROUND,
    height: '100%',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    ...(isWeb && {
      // doesn't seem to do much on android for some reason, avoid the potential perf hit, investigate later
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.8,
      shadowRadius: 10,
      elevation: 10,
    }),
  },
  modalContainer: {
    flex: 1,
    height: '100%',
    overflow: 'hidden',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    ...(isWeb && { alignItems: 'center' }),
  },
  scrollContainer: {
    alignItems: 'center',
  },
  scrollStyle: {
    width: '100%',
    backgroundColor: STANDARD_BACKGROUND,
  },
  wrappedChildren: {
    width: '100%',
    flexGrow: 1,
  },
  footerWrap: {
    padding: spacing.$4,
    paddingBottom: 0,
    borderTopWidth: 1,
    borderColor: theme.token('border-color-options'),
    backgroundColor: FOOTER_HIGHLIGHT,
    alignItems: 'center',
    width: '100%',
  },
  footerInner: {
    alignItems: 'center',
    width: '100%',
  },
});
